import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { getLocations, getAlerts, getWeatherByCoordinates, getWeather } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { Card, CardHeader, CardTitle, CardContent, LoadingState, WeatherIcon, Spinner, SEO } from '@/components';
import { FaChevronRight, FaWind, FaTint, FaMapMarkerAlt, FaCrosshairs, FaStar, FaRegStar } from 'react-icons/fa';
import type { Location } from '@miweather/shared';
import { useUserLocation, useFavorites } from '@/hooks';

// City images
const cityImages: Record<string, string> = {
  'detroit': 'https://images.unsplash.com/photo-1534430480872-3498386e7856?w=400&h=250&fit=crop',
  'grand-rapids': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=250&fit=crop',
  'traverse-city': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=250&fit=crop',
  'marquette': 'https://images.unsplash.com/photo-1478827536114-da961b7f86d2?w=400&h=250&fit=crop',
  'lansing': 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=400&h=250&fit=crop',
  'ann-arbor': 'https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=400&h=250&fit=crop',
};
const defaultImage = 'https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=400&h=250&fit=crop';

export function HomePage() {
  const { favorites, toggleFavorite, isFavorite } = useFavorites();

  const { data: locations, isLoading } = useQuery({
    queryKey: queryKeys.locations.all,
    queryFn: getLocations,
  });

  const { data: alertsData, error: alertsError } = useQuery({
    queryKey: queryKeys.alerts.all,
    queryFn: getAlerts,
    refetchInterval: 5 * 60 * 1000,
  });

  if (isLoading) {
    return <LoadingState message="Loading..." />;
  }

  const activeAlerts = alertsData?.alerts || [];
  // Get favorite locations from both cities and counties
  const allLocations = [...(locations?.cities || []), ...(locations?.counties || [])];
  const favoriteLocations = allLocations.filter(loc => favorites.includes(loc.id));

  return (
    <div className="space-y-5">
      <SEO
        title="Michigan Weather Forecasts"
        description="Real-time weather forecasts for all 83 Michigan counties and major cities. Hourly and 7-day forecasts, weather alerts, and detailed conditions."
        path="/"
      />
      <h1 className="sr-only">Michigan Weather Forecasts</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left Column */}
        <div className="space-y-5">
          {/* Current Location Card */}
          <CurrentLocationCard />

          {/* Radar */}
          <Card variant="solid" className="overflow-hidden">
            <CardHeader>
              <CardTitle>Statewide Radar</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="aspect-[4/3] bg-[var(--color-background)] relative">
                <img
                  src="https://radar.weather.gov/ridge/standard/KGRR_loop.gif"
                  alt="Live Michigan weather radar showing precipitation across the state"
                  className="w-full h-full object-cover opacity-90"
                  loading="lazy"
                />
                <div className="absolute top-2 right-2 flex gap-1 text-[10px]">
                  <span className="bg-green-500 px-1">0</span>
                  <span className="bg-blue-400 px-1">20%</span>
                  <span className="bg-blue-600 px-1">50%</span>
                  <span className="bg-purple-600 px-1">75%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weather News */}
          <Card variant="solid">
            <CardHeader className="pb-2">
              <CardTitle>Weather News</CardTitle>
            </CardHeader>
            <CardContent>
              {alertsError ? (
                <p className="text-yellow-500 text-sm">
                  Unable to load alerts - check weather.gov for updates
                </p>
              ) : (
                <p className="text-white/60 text-sm">
                  {activeAlerts.length > 0
                    ? `${activeAlerts.length} active alert${activeAlerts.length !== 1 ? 's' : ''} in Michigan`
                    : 'No active weather alerts in Michigan'}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-5">
          {/* Favorites */}
          <div>
            <h2 className="text-lg font-bold mb-3">Favorites</h2>
            <div className="grid grid-cols-2 gap-3">
              {favoriteLocations.length > 0 ? (
                favoriteLocations.map((loc) => (
                  <CityCard
                    key={loc.id}
                    city={loc}
                    isFavorite={isFavorite(loc.id)}
                    onToggleFavorite={() => toggleFavorite(loc.id)}
                  />
                ))
              ) : (
                <p className="col-span-2 text-white/50 text-sm">No favorites yet. Browse regions to add some!</p>
              )}
            </div>
          </div>

          {/* Browse Regions */}
          <BrowseRegionsCard />
        </div>
      </div>
    </div>
  );
}

function CurrentLocationCard() {
  const { location: userLocation, loading: locationLoading, permissionDenied } = useUserLocation();

  const { data: weatherData, isLoading: weatherLoading } = useQuery({
    queryKey: queryKeys.weather.byCoordinates(userLocation?.lat || 0, userLocation?.lon || 0),
    queryFn: () => getWeatherByCoordinates(userLocation!.lat, userLocation!.lon),
    enabled: !!userLocation,
  });

  const isLoading = locationLoading || weatherLoading;
  const cityName = userLocation?.city || 'Unknown';
  const locationSource = userLocation?.source;
  const locationId = weatherData?.location?.id;

  // Use location-specific image or default
  const bgImage = cityImages[cityName.toLowerCase().replace(/\s+/g, '-')] || defaultImage;

  const cardContent = (
    <Card
      variant="image"
      className="relative h-56 cursor-pointer"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="absolute inset-0 card-image-overlay rounded-[var(--radius-2xl)]" />
      <div className="relative h-full p-4 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-xs font-medium text-white/70 uppercase tracking-wide">Current Location</p>
            {locationSource === 'gps' && (
              <FaCrosshairs className="text-xs text-green-400" title="GPS location" />
            )}
            {(locationSource === 'ip' || locationSource === 'default') && (
              <FaMapMarkerAlt className="text-xs text-yellow-400" title={permissionDenied ? "Location permission denied - using approximate location" : "Approximate location"} />
            )}
          </div>
          <h2 className="text-xl font-bold">
            {isLoading ? 'Loading...' : `${cityName}, MI`}
          </h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <Spinner size="lg" />
          </div>
        ) : weatherData ? (
          <div className="flex items-end justify-between">
            <div>
              <div className="text-5xl font-black">{weatherData.current.temperature ?? '--'}°F</div>
              <p className="text-white/90">{weatherData.current.conditions}</p>
            </div>

            <div className="flex gap-2">
              <div className="stat-box px-3 py-2 rounded-lg">
                <p className="text-[10px] text-white/60">Wind</p>
                <p className="text-sm font-bold flex items-center gap-1">
                  <FaWind className="text-xs text-white/60" /> {weatherData.current.windSpeed ?? '--'} mph {weatherData.current.windDirection}
                </p>
              </div>
              <div className="stat-box px-3 py-2 rounded-lg">
                <p className="text-[10px] text-white/60">Humidity</p>
                <p className="text-sm font-bold flex items-center gap-1">
                  <FaTint className="text-xs text-white/60" /> {weatherData.current.humidity}%
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-white/60">Unable to load weather</p>
        )}
      </div>
    </Card>
  );

  // Link to location page or coordinates page
  if (locationId && locationId !== 'custom') {
    // Known location - link to location page
    return (
      <Link to="/weather/$locationId" params={{ locationId }} className="block">
        {cardContent}
      </Link>
    );
  } else if (userLocation) {
    // Custom coordinates - link to coordinates page
    return (
      <Link
        to="/weather/coordinates"
        search={{ lat: String(userLocation.lat), lon: String(userLocation.lon), city: cityName }}
        className="block"
      >
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

interface CityCardProps {
  city: Location;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

function CityCard({ city, isFavorite = false, onToggleFavorite }: CityCardProps) {
  const image = cityImages[city.id] || defaultImage;

  // Fetch real weather data for this location
  const { data: weatherData, isLoading } = useQuery({
    queryKey: queryKeys.weather.byLocation(city.id),
    queryFn: () => getWeather(city.id),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const handleStarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite?.();
  };

  return (
    <Link to="/weather/$locationId" params={{ locationId: city.id }}>
      <Card
        variant="image"
        className="relative h-28 hover:ring-2 hover:ring-[var(--color-info)] transition-all cursor-pointer"
        style={{ backgroundImage: `url(${image})` }}
      >
        <div className="absolute inset-0 card-image-overlay rounded-[var(--radius-2xl)]" />
        <div className="relative h-full p-3 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-bold text-base">{city.name}</h3>
              {isLoading ? (
                <p className="text-xs text-white/50">Loading...</p>
              ) : weatherData ? (
                <p className="text-xs text-white/70">{weatherData.current.conditions}</p>
              ) : (
                <p className="text-xs text-white/50">--</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {onToggleFavorite && (
                <button
                  onClick={handleStarClick}
                  className="p-1 hover:scale-110 transition-transform"
                  aria-label={isFavorite ? `Remove ${city.name} from favorites` : `Add ${city.name} to favorites`}
                  aria-pressed={isFavorite}
                >
                  {isFavorite ? (
                    <FaStar className="text-yellow-400" aria-hidden="true" />
                  ) : (
                    <FaRegStar className="text-white/50 hover:text-yellow-400" aria-hidden="true" />
                  )}
                </button>
              )}
              {weatherData && (
                <WeatherIcon icon={weatherData.current.icon} size={24} className="text-white/80" />
              )}
            </div>
          </div>
          <div className="text-2xl font-black">
            {isLoading ? '--' : weatherData ? `${weatherData.current.temperature ?? '--'}°F` : '--'}
          </div>
        </div>
      </Card>
    </Link>
  );
}

function BrowseRegionsCard() {
  return (
    <Card variant="solid">
      <CardHeader className="pb-2">
        <CardTitle>Browse Regions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <RegionLink regionId="mid-michigan" label="Mid-Michigan" description="Lansing, Saginaw, Bay City" />
        <RegionLink regionId="northern-lp" label="Northern LP" description="Traverse City, Petoskey" />
        <RegionLink regionId="southeast" label="Southeast" description="Detroit, Ann Arbor, Flint" />
        <RegionLink regionId="southwest" label="Southwest" description="Kalamazoo, Battle Creek" />
        <RegionLink regionId="thumb" label="The Thumb" description="Port Huron, Bad Axe" />
        <RegionLink regionId="upper-peninsula" label="Upper Peninsula" description="Marquette, Sault Ste. Marie" />
        <RegionLink regionId="west" label="West" description="Grand Rapids, Holland, Muskegon" />
      </CardContent>
    </Card>
  );
}

function RegionLink({ regionId, label, description }: { regionId: string; label: string; description: string }) {
  return (
    <Link
      to="/region/$regionId"
      params={{ regionId }}
      className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-colors group"
    >
      <div>
        <p className="font-medium group-hover:text-[var(--color-info)] transition-colors">{label}</p>
        <p className="text-sm text-white/50">{description}</p>
      </div>
      <FaChevronRight className="text-white/30 group-hover:text-[var(--color-info)] transition-colors" />
    </Link>
  );
}
